/**
 * Panel "Entregables":
 *  - Admin: matriz actividad × club con badges de estado y botón para
 *    revisar/aprobar/rechazar.
 *  - Director: lista de actividades que requieren entrega para su club,
 *    con estado actual.
 */

import { useMemo, useState } from "react";
import { Card, CardContent, Badge, Button, EmptyState } from "@/components/ui";
import { FileCheck, Clock, CheckCircle, XCircle, Eye } from "lucide-react";
import {
  SUBMISSION_STATUS_LABEL,
  getDisplaySubmissionStatus,
  reviewSubmission,
  toJsDate,
  canDirectorEdit,
} from "@/lib/services/activitySubmissions";
import { toast } from "react-hot-toast";
import type {
  ActivitySubmissionField,
  ClubActivitySubmission,
  EventActivity,
  EventActivityCategory,
  SubmissionFieldValue,
  SubmissionFileRef,
} from "@/types";
import { SubmissionFormModal } from "./SubmissionFormModal";
import { SubmissionReviewModal } from "./SubmissionReviewModal";
import { useNow } from "@/hooks/useNow";
import { exportAoaToXlsx, exportMultiSheetXlsx } from "@/lib/utils/exportXlsx";

interface Props {
  eventId: string;
  activities: EventActivity[];
  categories: EventActivityCategory[];
  clubs: { id: string; name: string }[];
  submissions: ClubActivitySubmission[];
  isAdmin: boolean;
  directorClubId?: string;
  /** Nombre del club del director (necesario cuando el club aún no tiene inscripciones y por eso no aparece en `clubs`). */
  directorClubName?: string;
  onOpenSubmission?: (act: EventActivity) => void;
}

export function SubmissionsMatrixPanel({
  eventId,
  activities,
  categories,
  clubs,
  submissions,
  isAdmin,
  directorClubId,
  directorClubName,
}: Props) {
  // Tick cada 30s para que apertura/cierre se reflejen en la UI sin refresh.
  const tickNow = useNow(30_000);
  const submittables = useMemo(
    () => activities.filter((a) => a.submissionRequirements?.enabled),
    [activities],
  );
  const submissionsByKey = useMemo(() => {
    const map = new Map<string, ClubActivitySubmission>();
    submissions.forEach((s) => {
      map.set(`${s.activityId}_${s.clubId}`, s);
    });
    return map;
  }, [submissions]);

  const categoriesById = useMemo(() => {
    const m = new Map<string, EventActivityCategory>();
    categories.forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  const [reviewing, setReviewing] = useState<ClubActivitySubmission | null>(
    null,
  );
  const [editingMine, setEditingMine] = useState<EventActivity | null>(null);
  type StatusFilter =
    | "all"
    | "pending"
    | "submitted"
    | "approved"
    | "rejected"
    | "missing";
  const FILTER_STORAGE_KEY = `matrixStatusFilter:${eventId}`;
  const [statusFilter, setStatusFilterState] = useState<StatusFilter>(() => {
    if (typeof window === "undefined") return "all";
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    const allowed: StatusFilter[] = [
      "all",
      "pending",
      "submitted",
      "approved",
      "rejected",
      "missing",
    ];
    return allowed.includes(raw as StatusFilter)
      ? (raw as StatusFilter)
      : "all";
  });
  const setStatusFilter = (v: StatusFilter) => {
    setStatusFilterState(v);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(FILTER_STORAGE_KEY, v);
      } catch {
        // localStorage puede estar bloqueado (modo privado, cuota llena).
      }
    }
  };

  if (submittables.length === 0) {
    return (
      <EmptyState
        icon={<FileCheck className="w-12 h-12 text-gray-400" />}
        title="Sin entregables configurados"
        description="Activa 'Requiere entregable previo' al editar una actividad para que aparezca aquí."
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Vista DIRECTOR
  // ---------------------------------------------------------------------------
  if (!isAdmin) {
    if (!directorClubId) {
      return (
        <EmptyState
          icon={<FileCheck className="w-12 h-12 text-gray-400" />}
          title="No tienes club asignado"
          description="Debes ser director de un club para realizar entregas."
        />
      );
    }
    return (
      <div className="space-y-3">
        <DirectorSummary
          submittables={submittables}
          submissionsByKey={submissionsByKey}
          directorClubId={directorClubId}
        />
        {submittables.map((act) => {
          const sub = submissionsByKey.get(`${act.id}_${directorClubId}`);
          const cat = categoriesById.get(act.categoryId);
          const opensAt = act.submissionRequirements?.opensAt;
          const deadline = act.submissionRequirements?.deadline;
          const opensDate = toJsDate(opensAt);
          const dl = toJsDate(deadline);
          const now = tickNow;
          const notOpenYet =
            opensDate !== undefined && opensDate.getTime() > now;
          let timing: { label: string; tone: "ok" | "warn" | "late" } | null =
            null;
          if (notOpenYet && opensDate) {
            const diff = opensDate.getTime() - now;
            const days = Math.floor(diff / 86400000);
            const hours = Math.floor((diff % 86400000) / 3600000);
            const mins = Math.floor((diff % 3600000) / 60000);
            timing = {
              label:
                days > 0
                  ? `Abre en ${days}d ${hours}h`
                  : hours > 0
                    ? `Abre en ${hours}h ${mins}m`
                    : `Abre en ${mins}m`,
              tone: "warn",
            };
          } else if (dl) {
            const diff = dl.getTime() - now;
            if (diff < 0) {
              const days = Math.floor(-diff / 86400000);
              timing = { label: `Pasó hace ${days}d`, tone: "late" };
            } else {
              const days = Math.floor(diff / 86400000);
              const hours = Math.floor((diff % 86400000) / 3600000);
              timing = {
                label: `${days}d ${hours}h`,
                tone: days < 2 ? "warn" : "ok",
              };
            }
          }
          const timingCls = timing
            ? {
                ok: "text-green-700 bg-green-50 border-green-200",
                warn: "text-amber-700 bg-amber-50 border-amber-200",
                late: "text-red-700 bg-red-50 border-red-200",
              }[timing.tone]
            : "";
          return (
            <Card key={act.id}>
              <CardContent
                className="p-4"
                style={{ borderLeft: `4px solid ${cat?.color ?? "#7c3aed"}` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">
                        {act.name}
                      </h4>
                      {cat && <Badge variant="secondary">{cat.name}</Badge>}
                      <StatusBadge submission={sub} deadline={deadline} />
                      {timing && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded border ${timingCls}`}
                        >
                          <Clock className="w-3 h-3 inline mr-1" />
                          {timing.label}
                        </span>
                      )}
                    </div>
                    {act.description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {act.description}
                      </p>
                    )}
                    {sub?.reviewNotes &&
                      getDisplaySubmissionStatus(sub) === "rejected" && (
                        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">
                          <strong>Motivo del rechazo:</strong> {sub.reviewNotes}
                        </div>
                      )}
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setEditingMine(act)}
                  >
                    {(() => {
                      const perm = canDirectorEdit(act, sub);
                      if (!perm.allowed) return "Ver";
                      return sub ? "Ver / editar" : "Entregar";
                    })()}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {editingMine && (
          <SubmissionFormModal
            isOpen={true}
            onClose={() => setEditingMine(null)}
            eventId={eventId}
            activity={editingMine}
            clubId={directorClubId}
            clubName={
              clubs.find((c) => c.id === directorClubId)?.name ??
              directorClubName ??
              ""
            }
            existing={submissionsByKey.get(
              `${editingMine.id}_${directorClubId}`,
            )}
          />
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Vista ADMIN — matriz actividad × club
  // ---------------------------------------------------------------------------
  // #14: orden estable de columnas (clubes) por nombre, sin mutar la prop.
  const sortedClubs = useMemo(
    () => [...clubs].sort((a, b) => a.name.localeCompare(b.name, "es")),
    [clubs],
  );
  // Re-shadow para evitar tocar todas las referencias internas de "clubs".
  const clubsForMatrix = sortedClubs;
  // Totales globales y por club/actividad
  const totalCells = submittables.length * clubsForMatrix.length;
  let g_approved = 0,
    g_submitted = 0,
    g_rejected = 0,
    g_draft = 0,
    g_missing = 0,
    g_late = 0;
  const perClub = new Map<
    string,
    {
      approved: number;
      submitted: number;
      rejected: number;
      missing: number;
      late: number;
    }
  >();
  const perActivity = new Map<
    string,
    {
      approved: number;
      submitted: number;
      rejected: number;
      missing: number;
      late: number;
    }
  >();
  clubsForMatrix.forEach((c) =>
    perClub.set(c.id, {
      approved: 0,
      submitted: 0,
      rejected: 0,
      missing: 0,
      late: 0,
    }),
  );
  submittables.forEach((a) =>
    perActivity.set(a.id, {
      approved: 0,
      submitted: 0,
      rejected: 0,
      missing: 0,
      late: 0,
    }),
  );
  submittables.forEach((act) => {
    clubsForMatrix.forEach((club) => {
      const sub = submissionsByKey.get(`${act.id}_${club.id}`);
      const pc = perClub.get(club.id)!;
      const pa = perActivity.get(act.id)!;
      if (!sub) {
        g_missing++;
        pc.missing++;
        pa.missing++;
        return;
      }
      if (sub.isLate) {
        g_late++;
        pc.late++;
        pa.late++;
      }
      const d = getDisplaySubmissionStatus(sub);
      if (d === "approved") {
        g_approved++;
        pc.approved++;
        pa.approved++;
      } else if (d === "submitted") {
        g_submitted++;
        pc.submitted++;
        pa.submitted++;
      } else if (d === "rejected") {
        g_rejected++;
        pc.rejected++;
        pa.rejected++;
      } else {
        g_draft++;
      }
    });
  });
  const completionPct =
    totalCells === 0 ? 0 : Math.round((g_approved / totalCells) * 100);

  const cellMatchesFilter = (sub?: ClubActivitySubmission): boolean => {
    if (statusFilter === "all") return true;
    if (statusFilter === "missing") return !sub;
    if (!sub) return false;
    const d = getDisplaySubmissionStatus(sub);
    if (statusFilter === "pending") return d === "draft" || d === "submitted";
    return d === statusFilter;
  };

  // #15: exportar respuestas (form values) por actividad — una sección por
  // actividad con columnas por campo del formulario.
  const exportResponsesCsv = (activityId: "all" | string) => {
    const fmtDate = (ts: unknown): string => {
      const d = ts ? toJsDate(ts as never) : undefined;
      return d ? d.toLocaleString("es-CO") : "";
    };
    const formatValue = (
      val: SubmissionFieldValue | undefined,
      type: ActivitySubmissionField["type"],
    ): string => {
      if (val === undefined || val === null || val === "") return "";
      if (type === "file") {
        const f = val as SubmissionFileRef;
        return f?.url ?? "";
      }
      if (type === "multi_file") {
        const arr = (val as SubmissionFileRef[]) || [];
        return arr
          .map((f) => f?.url ?? "")
          .filter(Boolean)
          .join(" | ");
      }
      if (type === "multi_select" && Array.isArray(val)) {
        return (val as string[]).join(" | ");
      }
      if (type === "boolean") return val ? "Sí" : "No";
      if (type === "date") {
        const d = toJsDate(val as never);
        if (d) return d.toLocaleDateString("es-CO");
      }
      return String(val);
    };

    const targets =
      activityId === "all"
        ? submittables
        : submittables.filter((a) => a.id === activityId);

    const sheets = targets.map((act) => {
      const fields = [...(act.submissionRequirements?.fields ?? [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      );
      const headers = [
        "Club",
        "Estado",
        "Tarde",
        "Enviado por",
        "Enviado el",
        "Última edición",
        "Revisión",
        "Notas revisor",
        ...fields.map((f) => f.label || f.id),
      ];
      const rows = clubsForMatrix.map((club) => {
        const sub = submissionsByKey.get(`${act.id}_${club.id}`);
        const statusLbl = sub
          ? (SUBMISSION_STATUS_LABEL[getDisplaySubmissionStatus(sub)] ??
            sub.status)
          : "Sin entregar";
        const obj: Record<string, unknown> = {
          Club: club.name,
          Estado: statusLbl,
          Tarde: sub?.isLate ? "Sí" : "",
          "Enviado por": sub?.submittedByName ?? "",
          "Enviado el": fmtDate(sub?.submittedAt),
          "Última edición": fmtDate(sub?.lastEditedAt),
          Revisión: sub?.reviewStatus ?? "",
          "Notas revisor": sub?.reviewNotes ?? "",
        };
        fields.forEach((f) => {
          obj[f.label || f.id] = formatValue(sub?.values?.[f.id], f.type);
        });
        return obj;
      });
      return {
        name: act.name,
        rows,
        columns: headers.map((h) => ({ header: h, key: h })),
      };
    });

    const slug =
      activityId === "all"
        ? "todas"
        : (submittables.find((a) => a.id === activityId)?.name ?? activityId)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
    const filename = `respuestas-${slug}-${new Date().toISOString().slice(0, 10)}`;
    if (sheets.length === 0) {
      exportAoaToXlsx({ aoa: [["Sin actividades"]], filename });
      return;
    }
    exportMultiSheetXlsx(sheets, filename);
  };

  // #13: exportar matriz a Excel con totales por fila/columna y resumen global.
  const exportCsv = () => {
    const statusShort: Record<string, string> = {
      approved: "aprobada",
      submitted: "enviada",
      rejected: "rechazada",
      draft: "borrador",
      not_delivered: "sin",
    };
    const head = [
      "Actividad",
      "Categoría",
      "Fecha límite",
      ...clubsForMatrix.map((c) => c.name),
      "Aprobadas",
      "En revisión",
      "Rechazadas",
      "Sin entregar",
      "Tardías",
    ];
    const aoa: unknown[][] = [head];
    submittables.forEach((act) => {
      const cat = categoriesById.get(act.categoryId);
      const dl = act.submissionRequirements?.deadline
        ? (toJsDate(act.submissionRequirements.deadline)?.toLocaleString(
            "es-CO",
          ) ?? "")
        : "";
      const pa = perActivity.get(act.id)!;
      const cells = clubsForMatrix.map((c) => {
        const sub = submissionsByKey.get(`${act.id}_${c.id}`);
        if (!sub) return statusShort.not_delivered;
        const d = getDisplaySubmissionStatus(sub);
        return statusShort[d] + (sub.isLate ? " (tarde)" : "");
      });
      aoa.push([
        act.name,
        cat?.name ?? "",
        dl,
        ...cells,
        pa.approved,
        pa.submitted,
        pa.rejected,
        pa.missing,
        pa.late,
      ]);
    });
    const totalRow: unknown[] = ["Total por club", "", ""];
    clubsForMatrix.forEach((c) => {
      const pc = perClub.get(c.id)!;
      totalRow.push(
        `${pc.approved}✓ ${pc.submitted}⚠ ${pc.rejected}✗ ${pc.missing}- ${pc.late}t`,
      );
    });
    totalRow.push(g_approved, g_submitted, g_rejected, g_missing, g_late);
    aoa.push(totalRow);
    aoa.push([]);
    aoa.push([
      "Resumen global",
      `${completionPct}% completado`,
      `${totalCells} celdas`,
    ]);

    exportAoaToXlsx({
      aoa,
      filename: `entregables-${eventId}-${new Date().toISOString().slice(0, 10)}`,
      sheetName: "Matriz",
    });
  };

  return (
    <div className="space-y-3">
      {/* Resumen + leyenda + filtro */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Estado global de entregables
              </p>
              <p className="text-xs text-gray-500">
                {g_approved} aprobadas / {totalCells} celdas
                {g_late > 0 && ` · ${g_late} tardía(s)`}
              </p>
            </div>
            <div className="text-2xl font-bold text-purple-700">
              {completionPct}%
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <select
              aria-label="Actividad para exportar respuestas"
              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 bg-white max-w-[220px]"
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                exportResponsesCsv(v);
                e.target.value = "";
              }}
            >
              <option value="">⬇ Exportar respuestas (Excel)…</option>
              <option value="all">Todas las actividades</option>
              {submittables.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={exportCsv}
              className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
              title="Descargar matriz de estado en Excel"
            >
              ⬇ Exportar matriz (Excel)
            </button>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {(
              [
                {
                  key: "all",
                  label: `Todos (${totalCells})`,
                  cls: "bg-gray-100 text-gray-700",
                },
                {
                  key: "missing",
                  label: `Sin entregar (${g_missing})`,
                  cls: "bg-gray-100 text-gray-600",
                },
                {
                  key: "submitted",
                  label: `En revisión (${g_submitted})`,
                  cls: "bg-yellow-100 text-yellow-800",
                },
                {
                  key: "rejected",
                  label: `Rechazadas (${g_rejected})`,
                  cls: "bg-red-100 text-red-800",
                },
                {
                  key: "approved",
                  label: `Aprobadas (${g_approved})`,
                  cls: "bg-green-100 text-green-800",
                },
              ] as const
            ).map((opt) => {
              const active = statusFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setStatusFilter(opt.key)}
                  className={`px-2 py-1 rounded border transition ${
                    active
                      ? "border-purple-500 ring-1 ring-purple-300 " + opt.cls
                      : "border-transparent " + opt.cls + " hover:opacity-80"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
            {g_draft > 0 && (
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-800">
                {g_draft} borrador(es)
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500">
            Click en cualquier celda con entrega para revisar / aprobar /
            rechazar. Las celdas vacías indican que el club aún no ha enviado.
          </p>
        </CardContent>
      </Card>

      {/* Matriz */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-20">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-30 min-w-[200px]">
                  Actividad
                </th>
                {clubsForMatrix.map((c) => (
                  <th
                    key={c.id}
                    className="text-center p-2 font-medium text-gray-600 min-w-[120px]"
                  >
                    {c.name}
                  </th>
                ))}
                <th className="text-center p-2 font-semibold text-gray-700 min-w-[100px] bg-gray-100">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {submittables.map((act) => {
                const cat = categoriesById.get(act.categoryId);
                const deadline = act.submissionRequirements?.deadline;
                const pa = perActivity.get(act.id)!;
                return (
                  <tr key={act.id} className="border-t border-gray-100">
                    <td
                      className="p-3 sticky left-0 bg-white z-10 border-r border-gray-100"
                      style={{
                        borderLeft: `4px solid ${cat?.color ?? "#7c3aed"}`,
                      }}
                    >
                      <div className="font-medium text-gray-900">
                        {act.name}
                      </div>
                      {deadline && (
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" />
                          {toJsDate(deadline)?.toLocaleDateString("es-CO")}
                        </div>
                      )}
                    </td>
                    {clubsForMatrix.map((club) => {
                      const sub = submissionsByKey.get(`${act.id}_${club.id}`);
                      const matches = cellMatchesFilter(sub);
                      return (
                        <td
                          key={club.id}
                          className={`p-2 text-center align-middle ${
                            matches ? "" : "opacity-25"
                          }`}
                        >
                          {sub ? (
                            <CellBadge
                              submission={sub}
                              deadline={deadline}
                              onClick={() => setReviewing(sub)}
                            />
                          ) : (
                            (() => {
                              const dl = toJsDate(deadline);
                              const expired = !!dl && dl.getTime() < Date.now();
                              return (
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                    expired
                                      ? "bg-red-50 text-red-600 border border-red-200"
                                      : "bg-gray-100 text-gray-400"
                                  }`}
                                  title={
                                    expired
                                      ? "Sin entregar \u00b7 fecha l\u00edmite vencida"
                                      : "Sin entrega"
                                  }
                                >
                                  {expired ? "\u26a0 sin" : "\u2014"}
                                </span>
                              );
                            })()
                          )}
                        </td>
                      );
                    })}
                    <td className="p-2 text-center text-xs bg-gray-50 border-l border-gray-200">
                      <RowTotals stats={pa} total={clubsForMatrix.length} />
                    </td>
                  </tr>
                );
              })}
              {/* Fila de totales por club */}
              <tr className="border-t-2 border-gray-300 bg-gray-50">
                <td className="p-3 sticky left-0 bg-gray-50 z-10 font-semibold text-gray-700 text-xs">
                  Total por club
                </td>
                {clubsForMatrix.map((club) => {
                  const pc = perClub.get(club.id)!;
                  return (
                    <td
                      key={club.id}
                      className="p-2 text-center text-xs align-top"
                    >
                      <ColumnTotals stats={pc} total={submittables.length} />
                    </td>
                  );
                })}
                <td className="p-2 bg-gray-100" />
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {reviewing && (
        <SubmissionReviewModal
          isOpen={true}
          onClose={() => setReviewing(null)}
          submission={reviewing}
          activity={
            activities.find((a) => a.id === reviewing.activityId) ?? null
          }
          onReview={async (decision, notes) => {
            try {
              await reviewSubmission({
                submissionId: reviewing.id,
                decision,
                notes,
              });
              toast.success(decision === "approved" ? "Aprobado" : "Rechazado");
              setReviewing(null);
            } catch (e) {
              toast.error((e as Error).message);
            }
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Helpers visuales
// ============================================================================

function StatusBadge({
  submission,
  deadline,
}: {
  submission?: ClubActivitySubmission;
  deadline?: Date | { toMillis: () => number };
}) {
  if (!submission) {
    const dl = toJsDate(deadline as never);
    const expired = dl && Date.now() > dl.getTime();
    return expired ? (
      <Badge variant="danger">No entregado · ⚠ vencido</Badge>
    ) : (
      <Badge variant="default">Pendiente</Badge>
    );
  }
  const lateTag = submission.isLate ? " · tarde" : "";
  const display = getDisplaySubmissionStatus(submission);
  switch (display) {
    case "draft":
      return <Badge variant="default">Borrador</Badge>;
    case "submitted":
      return <Badge variant="warning">{`Enviado${lateTag}`}</Badge>;
    case "approved":
      return <Badge variant="success">{`Aprobado${lateTag}`}</Badge>;
    case "rejected":
      return <Badge variant="danger">{`Rechazado${lateTag}`}</Badge>;
    default:
      return (
        <Badge variant="default">{SUBMISSION_STATUS_LABEL[display]}</Badge>
      );
  }
}

function CellBadge({
  submission,
  deadline,
  onClick,
}: {
  submission?: ClubActivitySubmission;
  deadline?: Date | { toMillis: () => number };
  onClick: () => void;
}) {
  if (!submission) {
    const dl = toJsDate(deadline as never);
    const expired = dl && Date.now() > dl.getTime();
    return (
      <span
        className={`inline-block px-2 py-1 rounded text-xs ${
          expired ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"
        }`}
        title={expired ? "Vencido sin entregar" : "Pendiente"}
      >
        {expired ? "⚠" : "—"}
      </span>
    );
  }
  const display = getDisplaySubmissionStatus(submission);
  const icon =
    display === "approved" ? (
      <CheckCircle className="w-3 h-3" />
    ) : display === "rejected" ? (
      <XCircle className="w-3 h-3" />
    ) : (
      <Eye className="w-3 h-3" />
    );
  const cls =
    display === "approved"
      ? "bg-green-100 text-green-700 hover:bg-green-200"
      : display === "rejected"
        ? "bg-red-100 text-red-700 hover:bg-red-200"
        : display === "submitted"
          ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${cls}`}
      title={`${SUBMISSION_STATUS_LABEL[display]}${
        submission.isLate ? " (tarde)" : ""
      } — Click para revisar`}
    >
      {icon}
      {SUBMISSION_STATUS_LABEL[display].slice(0, 3)}
      {submission.isLate && "⚠"}
    </button>
  );
}

// ============================================================================
// DirectorSummary — Tarjeta resumen al inicio de la lista del director
// ============================================================================

function DirectorSummary({
  submittables,
  submissionsByKey,
  directorClubId,
}: {
  submittables: EventActivity[];
  submissionsByKey: Map<string, ClubActivitySubmission>;
  directorClubId: string;
}) {
  const total = submittables.length;
  let approved = 0;
  let submitted = 0;
  let rejected = 0;
  let drafts = 0;
  let pending = 0;
  let upcomingLate = 0;
  const now = Date.now();
  submittables.forEach((act) => {
    const sub = submissionsByKey.get(`${act.id}_${directorClubId}`);
    const dl = toJsDate(act.submissionRequirements?.deadline);
    const display = sub ? getDisplaySubmissionStatus(sub) : null;
    if (!sub) {
      pending++;
      if (dl && dl.getTime() < now) upcomingLate++;
    } else if (display === "approved") approved++;
    else if (display === "rejected") rejected++;
    else if (display === "submitted") submitted++;
    else if (sub.status === "draft") drafts++;
  });
  const completedPct = total === 0 ? 0 : Math.round((approved / total) * 100);

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-gray-900">Mis entregas</p>
            <p className="text-xs text-gray-500">
              {approved} de {total} aprobadas
            </p>
          </div>
          <div className="text-2xl font-bold text-purple-700">
            {completedPct}%
          </div>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${completedPct}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {pending > 0 && (
            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">
              Sin entregar: <b>{pending}</b>
            </span>
          )}
          {upcomingLate > 0 && (
            <span className="px-2 py-0.5 rounded bg-red-100 text-red-700">
              Vencidas sin entregar: <b>{upcomingLate}</b>
            </span>
          )}
          {drafts > 0 && (
            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">
              Borradores: <b>{drafts}</b>
            </span>
          )}
          {submitted > 0 && (
            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
              En revisión: <b>{submitted}</b>
            </span>
          )}
          {rejected > 0 && (
            <span className="px-2 py-0.5 rounded bg-red-100 text-red-800">
              Rechazadas (corregir): <b>{rejected}</b>
            </span>
          )}
          {approved > 0 && (
            <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">
              Aprobadas: <b>{approved}</b>
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Totales por fila / columna en la matriz admin
// ============================================================================

type Stats = {
  approved: number;
  submitted: number;
  rejected: number;
  missing: number;
  late: number;
};

function RowTotals({ stats, total }: { stats: Stats; total: number }) {
  const pct = total === 0 ? 0 : Math.round((stats.approved / total) * 100);
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="font-semibold text-gray-700">
        {stats.approved}/{total}
      </span>
      <div className="w-16 h-1 bg-gray-200 rounded">
        <div
          className={`h-full rounded ${
            pct === 100 ? "bg-green-500" : "bg-purple-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {(stats.submitted > 0 || stats.rejected > 0 || stats.late > 0) && (
        <div className="flex gap-1 text-[10px]">
          {stats.submitted > 0 && (
            <span className="text-yellow-700" title="En revisión">
              ⏳{stats.submitted}
            </span>
          )}
          {stats.rejected > 0 && (
            <span className="text-red-700" title="Rechazadas">
              ✗{stats.rejected}
            </span>
          )}
          {stats.late > 0 && (
            <span className="text-amber-700" title="Tardías">
              ⚠{stats.late}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ColumnTotals({ stats, total }: { stats: Stats; total: number }) {
  const pct = total === 0 ? 0 : Math.round((stats.approved / total) * 100);
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="font-semibold text-gray-700">
        {stats.approved}/{total}
      </span>
      <div className="w-16 h-1 bg-gray-200 rounded">
        <div
          className={`h-full rounded ${
            pct === 100 ? "bg-green-500" : "bg-purple-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex gap-1 text-[10px] text-gray-500">
        {stats.missing > 0 && (
          <span title="Sin entregar">·{stats.missing}</span>
        )}
        {stats.late > 0 && (
          <span className="text-amber-700" title="Tardías">
            ⚠{stats.late}
          </span>
        )}
      </div>
    </div>
  );
}
